import { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import api, { fileUrl } from "@/lib/api";
import { gstSuffix } from "@/lib/constants";
import { toast } from "sonner";
import { ImageIcon, FileText, Check, GitMerge, Loader2, Crown } from "lucide-react";

const px = (p) => (p.liquor_image_width && p.liquor_image_height ? p.liquor_image_width * p.liquor_image_height : 0);

function pickDefaults(items) {
  const primary = [...items].sort((a, b) => (b.times_added || 0) - (a.times_added || 0) || String(a.created_at || "").localeCompare(String(b.created_at || "")))[0];
  const withImg = items.filter((p) => p.liquor_image_url);
  const liquor = withImg.sort((a, b) => px(b) - px(a) || (a.id === primary.id ? -1 : 1))[0] || primary;
  const withRep = items.filter((p) => p.report_url);
  const report = withRep.sort((a, b) => (b.report_content_type === "application/pdf") - (a.report_content_type === "application/pdf") || (a.id === primary.id ? -1 : 1))[0] || primary;
  return { primaryId: primary.id, liquorId: liquor.id, reportId: report.id };
}

function Pick({ on, onClick, label, testid, disabled }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid={testid}
      className={`w-full inline-flex items-center justify-center gap-1.5 rounded-md border px-2 py-1 text-[11px] font-semibold transition-colors ${
        on ? "bg-[#0B3B2E] text-[#F7F2E7] border-[#0B3B2E]" : "bg-white text-[#14513D] border-[#C9A34A]/40 hover:bg-[#C9A34A]/10"
      } disabled:opacity-40 disabled:cursor-not-allowed`}
    >
      {on && <Check className="h-3 w-3" />} {label}
    </button>
  );
}

export default function MergeProducts({ items, open, onOpenChange, onMerged }) {
  const [sel, setSel] = useState({ primaryId: "", liquorId: "", reportId: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open && items?.length > 1) setSel(pickDefaults(items)); }, [open, items]);
  const others = useMemo(() => (items || []).filter((p) => p.id !== sel.primaryId), [items, sel.primaryId]);
  if (!items || items.length < 2) return null;
  const lot = items[0].lot_no;

  const merge = async () => {
    setBusy(true);
    try {
      await api.post("/products/merge", { primary_id: sel.primaryId, other_ids: others.map((p) => p.id), liquor_from_id: sel.liquorId, report_from_id: sel.reportId });
      toast.success(`Merged ${items.length} products into one · ${others.length} moved to Recycle Bin`);
      onOpenChange(false);
      onMerged && onMerged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Merge failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto" data-testid="merge-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-[#0B3B2E] flex items-center gap-2"><GitMerge className="h-5 w-5 text-[#C9A34A]" /> Merge duplicates · Lot {lot}</DialogTitle>
          <DialogDescription>Choose the record to keep, then the liquor image and tasting report worth keeping. Blank fields are filled from the other copies; catalogue usage moves to the kept record; the rest go to the Recycle Bin.</DialogDescription>
        </DialogHeader>

        <div className="grid gap-4" style={{ gridTemplateColumns: `repeat(${Math.min(items.length, 3)}, minmax(0, 1fr))` }}>
          {items.map((p) => {
            const isPrimary = p.id === sel.primaryId;
            return (
              <div key={p.id} className={`rounded-xl border p-3 space-y-3 ${isPrimary ? "border-[#C9A34A] bg-[#FAF7F0] ring-1 ring-[#C9A34A]/50" : "border-[#C9A34A]/25 bg-white"}`} data-testid={`merge-col-${p.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-semibold text-[#0B3B2E] truncate">{p.brand_group || p.garden || p.name || "Tea"}</div>
                    <div className="text-[11px] text-[#14513D]/70 truncate">{[p.garden, p.grade, p.invoice_no ? `Inv ${p.invoice_no}` : null].filter(Boolean).join(" · ") || "—"}</div>
                    <div className="text-[11px] text-[#14513D]/70">{p.price_per_kg ? `₹${p.price_per_kg}/kg${gstSuffix(p)}` : "No price"} · Added {p.times_added || 0}×</div>
                    <div className="text-[10px] text-[#14513D]/50">Created {p.created_at ? new Date(p.created_at).toLocaleDateString() : "—"}</div>
                  </div>
                  {isPrimary && <Badge className="bg-[#C9A34A] text-[#0B3B2E] hover:bg-[#C9A34A] gap-1 shrink-0"><Crown className="h-3 w-3" /> Kept</Badge>}
                </div>
                <Pick on={isPrimary} onClick={() => setSel((s) => ({ ...s, primaryId: p.id }))} label={isPrimary ? "Record to keep" : "Keep this record"} testid={`merge-primary-${p.id}`} />

                <div>
                  <div className="text-[10px] font-bold uppercase tracking-wide text-[#C9A34A] mb-1">Liquor image {p.liquor_image_width ? <span className="text-[#14513D]/60 font-normal normal-case">· {p.liquor_image_width}×{p.liquor_image_height} px</span> : null}</div>
                  <div className={`aspect-square rounded-lg border bg-white grid place-items-center overflow-hidden ${sel.liquorId === p.id ? "border-[#0B3B2E] ring-2 ring-[#0B3B2E]/30" : "border-[#C9A34A]/30"}`}>
                    {p.liquor_image_url ? <img src={fileUrl(p.liquor_image_url)} alt="liquor" className="w-full h-full object-contain" /> : <ImageIcon className="h-7 w-7 text-[#C9A34A]/40" />}
                  </div>
                  <div className="mt-1.5"><Pick on={sel.liquorId === p.id} disabled={!p.liquor_image_url} onClick={() => setSel((s) => ({ ...s, liquorId: p.id }))} label="Use this liquor image" testid={`merge-liquor-${p.id}`} /></div>
                </div>

                <div>
                  <div className="text-[10px] font-bold uppercase tracking-wide text-[#C9A34A] mb-1">Tasting report {p.report_url ? <span className="text-[#14513D]/60 font-normal normal-case">· {p.report_content_type === "application/pdf" ? "PDF" : "Image"}</span> : null}</div>
                  <div className={`h-36 rounded-lg border bg-white grid place-items-center overflow-hidden ${sel.reportId === p.id ? "border-[#0B3B2E] ring-2 ring-[#0B3B2E]/30" : "border-[#C9A34A]/30"}`}>
                    {p.report_url ? (
                      p.report_content_type === "application/pdf" ? <iframe title="report" src={fileUrl(p.report_url)} className="w-full h-full pointer-events-none" /> : <img src={fileUrl(p.report_url)} alt="report" className="w-full h-full object-contain" />
                    ) : <FileText className="h-7 w-7 text-[#C9A34A]/40" />}
                  </div>
                  <div className="mt-1.5"><Pick on={sel.reportId === p.id} disabled={!p.report_url} onClick={() => setSel((s) => ({ ...s, reportId: p.id }))} label="Use this report" testid={`merge-report-${p.id}`} /></div>
                </div>
              </div>
            );
          })}
        </div>

        <DialogFooter className="gap-2 sm:gap-2 items-center">
          <span className="text-xs text-[#14513D]/70 mr-auto" data-testid="merge-summary">{items.length} → 1 · {others.length} duplicate{others.length > 1 ? "s" : ""} to Recycle Bin · usage {items.reduce((n, p) => n + (p.times_added || 0), 0)}× consolidated</span>
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="merge-cancel">Cancel</Button>
          <Button onClick={merge} disabled={busy} data-testid="merge-confirm" className="bg-[#0B3B2E] hover:bg-[#14513D] gap-1">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <GitMerge className="h-4 w-4" />} Merge into one
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
